/**
 * Standalone layout housekeeping rules and PO-confirmed messages (KAN-523).
 *
 * Covers:
 * - AC-1: Shared `isGlobalRoute(storeId, deviceId)` (`storeId === 'global' && deviceId === 'global'`).
 * - AC-3: Standalone tile deletion confirmation message and prefix-safe group matching.
 * - AC-4: Blocking tile ID rename once a tile has artwork (thumbnail or frame groups).
 * - AC-5: Revert to Global confirmation, button labels, and success message.
 * - AC-6: Blank starter confirmation message (keeps existing frame groups and files).
 * - AC-7: `hasStandaloneCanvas` check (`Array.isArray(configData?.standaloneTiles)`).
 * - AC-8: `_PP` (Print Preview) category support and shared `{baseName}_{timestamp}` anchor across `_FP`, `_PP`, `_FTB`, `_FPBG`.
 * - AC-9: Missing frame group error message on asset upload.
 * - AC-10: Longest-prefix frame group matching so prefix-ambiguous tile IDs (`a`, `a_b`) never mix groups.
 * - AC-11: Unsaved layout changes detection and Publish to Device warning message.
 *
 * Pure functions only — no Angular and no Firebase imports.
 */

const nonNegInt = (n: any): number => (Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0);

/** All PO-confirmed user-facing strings for KAN-523. */
export const STANDALONE_HOUSEKEEPING_MESSAGES = {
  revertTitle: 'Revert to the global layout?',
  revertBody:
    "This permanently deletes this machine's custom layout, frame groups, uploaded artwork and videos, and the machine will use the global layout. This cannot be undone.",
  revertConfirmLabel: 'Delete and revert',
  revertCancelLabel: 'Cancel',
  revertSuccess: 'Reverted to the global layout. Publish this machine to update it.',

  deleteTileConfirm: (frameGroups: number, files: number) =>
    `Delete this tile? Its ${nonNegInt(frameGroups)} frame groups and ${nonNegInt(files)} files will be deleted from this machine. This cannot be undone.`,
  deleteTileConfirmLabel: 'Delete tile',
  deleteTileCancelLabel: 'Cancel',

  tileIdChangeBlocked: "This tile already has artwork, so its ID can't be changed.",

  publishUnsavedWarning: 'You have unsaved layout changes. Publishing sends only the saved layout.',
  publishUnsavedConfirmLabel: 'Publish saved layout',
  publishUnsavedCancelLabel: 'Cancel',

  blankStartConfirm: 'Start with a blank layout? Existing frame groups and files on this machine are kept.',

  missingFrameGroupOnUpload:
    'The file was uploaded but its frame group no longer exists. Re-create the group and upload again.'
} as const;

/**
 * AC-1: Single shared test for whether a route targets the global configuration (`storeId === 'global' && deviceId === 'global'`).
 * A device route with an unassigned store (`/config/global/DEV-01`) is a device route, never global.
 */
export function isGlobalRoute(storeId: string | null | undefined, deviceId: string | null | undefined): boolean {
  return storeId === 'global' && deviceId === 'global';
}

/**
 * AC-7: True only when `photobooth_config/{deviceId}` holds a standalone canvas (`standaloneTiles` is an array).
 * Inheriting devices with only `customizedTiles` return false so the starter modal shows only the clone/blank options.
 */
export function hasStandaloneCanvas(configData: any | null | undefined): boolean {
  return Array.isArray(configData?.['standaloneTiles']);
}

/**
 * AC-4: True when a tile already has a thumbnail or at least one frame group, blocking tile ID changes.
 */
export function tileHasArtwork(
  tileId: string | null | undefined,
  tileThumbnails: Record<string, string | null | undefined> | null | undefined,
  frameGroupThemeIds: Iterable<string> | null | undefined,
  activeThumbnailUrl?: string | null
): boolean {
  if (!tileId) return false;
  if (activeThumbnailUrl) return true;
  if (tileThumbnails && tileThumbnails[tileId]) return true;
  if (frameGroupThemeIds) {
    for (const themeId of frameGroupThemeIds) {
      if (themeId === tileId) return true;
    }
  }
  return false;
}

/** AC-8: Supported frame asset variant categories per Blueprint 5.2. */
export type FrameVariantSuffix = '_FTB' | '_FP' | '_PP' | '_FPBG';

export interface FrameAssetCategory {
  id: FrameVariantSuffix;
  name: string;
  icon: string;
}

export const FRAME_ASSET_CATEGORIES: readonly FrameAssetCategory[] = [
  { id: '_FTB', name: 'Thumbnail', icon: '🖼️' },
  { id: '_FP', name: 'Preview', icon: '📱' },
  { id: '_PP', name: 'Print Preview', icon: '🖨️' },
  { id: '_FPBG', name: 'Background', icon: '🎨' }
] as const;

/** Maps a variant suffix to its property name on an `uploadedFrames` entry. */
export function variantSlotField(suffix: string): 'fpName' | 'ppName' | 'ftbName' | 'fpbgName' | null {
  switch (suffix.toUpperCase()) {
    case '_FP':
      return 'fpName';
    case '_PP':
      return 'ppName';
    case '_FTB':
      return 'ftbName';
    case '_FPBG':
      return 'fpbgName';
    default:
      return null;
  }
}

/** Strips `.png` extension and any trailing variant suffix (`_FPBG`, `_FTB`, `_FP`, `_PP`) from a filename. */
export function extractCleanBaseName(fileName: string): string {
  const withoutExt = fileName.replace(/\.png$/i, '');
  for (const suffix of ['_FPBG', '_FTB', '_FP', '_PP']) {
    if (withoutExt.toUpperCase().endsWith(suffix)) {
      return withoutExt.slice(0, withoutExt.length - suffix.length);
    }
  }
  return withoutExt;
}

/** Strips trailing variant suffix and `.png` from a stored generated asset filename to recover its `{baseName}_{timestamp}` frameId. */
export function extractFrameIdFromGeneratedName(generatedName: string): string {
  return extractCleanBaseName(generatedName);
}

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * AC-8: Resolves the shared `{baseName}_{timestamp}` anchor for an uploaded frame variant.
 * - If the file's clean base name already ends with `_{10+ digit timestamp}`, that anchor is preserved.
 * - Else if `existingUploadedFrames` already has an entry for `{cleanBase}_{timestamp}` whose slot for `suffix` is empty,
 *   that anchor is reused so `_FP`, `_PP`, `_FTB`, and `_FPBG` of one design share one `frameId`.
 * - Else (new design, or uploading the same variant suffix again for a second design with the same filename), mints `{cleanBase}_{nowMs}`.
 */
export function resolveFrameAnchor(
  fileName: string,
  suffix: string,
  existingUploadedFrames: any[] | null | undefined,
  nowMs: number = Date.now()
): { frameId: string; generatedFileName: string } {
  const cleanBase = extractCleanBaseName(fileName);
  const slot = variantSlotField(suffix);

  if (/_\d{10,}$/.test(cleanBase)) {
    return {
      frameId: cleanBase,
      generatedFileName: `${cleanBase}${suffix}.png`
    };
  }

  const frames = Array.isArray(existingUploadedFrames) ? existingUploadedFrames : [];
  if (slot) {
    const pattern = new RegExp(`^${escapeRegex(cleanBase)}_\\d+$`);
    for (let i = frames.length - 1; i >= 0; i--) {
      const entry = frames[i];
      if (entry && typeof entry === 'object' && typeof entry.frameId === 'string' && pattern.test(entry.frameId)) {
        if (!entry[slot]) {
          return {
            frameId: entry.frameId,
            generatedFileName: `${entry.frameId}${suffix}.png`
          };
        }
      }
    }
  }

  const frameId = `${cleanBase}_${nowMs}`;
  return {
    frameId,
    generatedFileName: `${frameId}${suffix}.png`
  };
}

/**
 * AC-10: Returns the LONGEST theme ID from `knownThemeIds` that prefixes a `frame_groups` doc ID (`{themeId}_{groupId}`).
 * Prevents prefix-ambiguous tile IDs (e.g. `a` and `a_b`) from mixing frame groups (`a_b_g1` matches `a_b`, not `a`).
 */
export function matchFrameGroupTheme(docId: string, knownThemeIds: Iterable<string>): string | null {
  if (!docId) return null;
  let best: string | null = null;
  for (const rawThemeId of knownThemeIds || []) {
    const t = typeof rawThemeId === 'string' ? rawThemeId : '';
    if (!t) continue;
    if (docId.startsWith(`${t}_`) && docId.length > t.length + 1) {
      if (best === null || t.length > best.length) {
        best = t;
      }
    }
  }
  return best;
}

/**
 * AC-10: Filters a collection of `frame_groups` documents to those belonging to `themeId` using longest-prefix resolution
 * across all known layout theme IDs.
 */
export function filterFrameGroupDocsForTheme<T extends { id: string }>(
  docs: T[],
  themeId: string,
  knownThemeIds: Iterable<string> = []
): Array<{ doc: T; frameId: string }> {
  if (!themeId) return [];
  const allThemes = new Set<string>([themeId]);
  for (const t of knownThemeIds || []) {
    if (typeof t === 'string' && t.trim() !== '') allThemes.add(t);
  }
  const out: Array<{ doc: T; frameId: string }> = [];
  for (const d of docs || []) {
    if (!d?.id) continue;
    const matched = matchFrameGroupTheme(d.id, allThemes);
    if (matched === themeId) {
      out.push({ doc: d, frameId: d.id.slice(themeId.length + 1) });
    }
  }
  return out;
}

/**
 * AC-10: Detects pairs of tile IDs where `{shorter}_` is a prefix of `{longer}_` (e.g. `a` and `a_b`).
 */
export function findPrefixAmbiguousTileIds(tileIds: Iterable<string>): Array<{ shorter: string; longer: string }> {
  const unique = [...new Set([...(tileIds || [])].filter(id => typeof id === 'string' && id.trim() !== ''))];
  const pairs: Array<{ shorter: string; longer: string }> = [];
  for (let i = 0; i < unique.length; i++) {
    for (let j = 0; j < unique.length; j++) {
      if (i === j) continue;
      const shorter = unique[i];
      const longer = unique[j];
      if (longer.startsWith(`${shorter}_`)) {
        pairs.push({ shorter, longer });
      }
    }
  }
  return pairs;
}

/**
 * AC-11: Canonical serialization of layout tiles for dirty-state comparison in the layout editor.
 */
export function serializeLayoutForDirtyCheck(tiles: any[] | null | undefined): string {
  const normalized = (tiles || []).map(t => ({
    id: t?.id || '',
    uid: t?.uid || '',
    x: t?.x ?? 0,
    y: t?.y ?? 0,
    cols: t?.cols ?? 1,
    rows: t?.rows ?? 1,
    properties: {
      title: t?.properties?.title || '',
      subTitle: t?.properties?.subTitle || '',
      price: Number(t?.properties?.price) || 0,
      priceClassicStrip: Number(t?.properties?.priceClassicStrip) || 0,
      priceMultiCut: Number(t?.properties?.priceMultiCut) || 0,
      cutStyleKey: t?.properties?.cutStyleKey || '',
      imageDirectory: t?.properties?.imageDirectory || '',
      tileType: t?.properties?.tileType || 'Theme',
      isCustomizable: !!t?.properties?.isCustomizable,
      isCustomized: !!t?.properties?.isCustomized,
      featuredThemeId: t?.properties?.featuredThemeId || '',
      featuredFrameId: t?.properties?.featuredFrameId || '',
      textPositionX: t?.properties?.textPositionX || 0,
      textPositionY: t?.properties?.textPositionY || 0,
      titleColor: t?.properties?.titleColor || '#ffffff',
      subtitleColor: t?.properties?.subtitleColor || '#e2e8f0',
      titleFontSize: t?.properties?.titleFontSize || 18,
      subtitleFontSize: t?.properties?.subtitleFontSize || 14,
      titleBold: !!t?.properties?.titleBold,
      titleItalic: !!t?.properties?.titleItalic,
      titleUnderline: !!t?.properties?.titleUnderline,
      subtitleBold: !!t?.properties?.subtitleBold,
      subtitleItalic: !!t?.properties?.subtitleItalic,
      subtitleUnderline: !!t?.properties?.subtitleUnderline
    }
  }));
  return JSON.stringify(normalized);
}

